import React, { useEffect, useState } from "react";
import {
	Outlet,
	Route,
	Routes,
	useLocation,
	useNavigate,
} from "react-router-dom";

import { tokens } from "../Application/theme";
import { ShowUpdatedProcedures } from "../Procedures/ShowUpdatedProcedures";
// import components
import { Badge, Tab, Tabs, styled, useTheme } from "@mui/material";

import { Translate } from "@/components/Application/Translate";
// import { EditTask } from "@/components/Task/EditTask";
import ProcedureUpdates from "@/components/Task/ProcedureUpdates";

import { UserContext } from "@/model/User/UserContext";

export const MaterialHandlingPage = () => {
	const navigateTo = useNavigate();
	const [selectedMainTab, setSelectedMainTab] = useState(0);
	const [selectedSecondaryTab, setSelectedSecondaryTab] = useState(0);
	const theme = useTheme();
	const colors = tokens(theme.palette.mode);
	const location = useLocation();

	const user = React.useContext(UserContext);

	const isProcedureSuggestionsAdministrator =
		user?.IsProcedureSuggestionsAdministrator;

	// Routing defined here
	useEffect(() => {
		// Set the main and secondary tabs based on the current path
		const path = location.pathname;

		// Logic for setting the main tab
		if (path.includes("/materialhandling/updates/")) {
			setSelectedMainTab(1); // Procedure Updates tab
			if (isProcedureSuggestionsAdministrator) {
				// Administrator logic
				if (path.includes("/newImprov")) setSelectedSecondaryTab(0);
				else if (path.includes("/myImprov")) setSelectedSecondaryTab(1);
				else if (path.includes("/verified")) setSelectedSecondaryTab(2);
				else if (path.includes("/declined")) setSelectedSecondaryTab(3);
			} else {
				// Non-administrator logic
				if (path.includes("/myImprov")) setSelectedSecondaryTab(0);
				else if (path.includes("/verified")) setSelectedSecondaryTab(1);
				else if (path.includes("/declined")) setSelectedSecondaryTab(2);
			}
		} else if (
			path.includes("/materialhandling/approval/pending") ||
			path.includes("/materialhandling/approval/approve/")
		) {
			setSelectedMainTab(2); // Pending Approval tab
		} else {
			setSelectedMainTab(0); // Default to Task tab
		}
	}, [location.pathname, isProcedureSuggestionsAdministrator]);

	const StyledBadge = styled(Badge)(({ theme, color }) => ({
		"& .MuiBadge-badge": {
			border: `1px solid ${theme.palette[color].main}`, // Use the color prop to set the border color
			padding: "0 4px",
			color: "black",
			backgroundColor: "transparent",
		},
	}));

	const StyledTab = styled((props) => (
		<Tab
			{...props}
			label={
				<div style={{ display: "flex", alignItems: "center" }}>
					{props.label}
					{props.badge && props.color && (
						<StyledBadge
							variant="standard" // Use standard variant as a base
							badgeContent={props.badge ? props.badge : ""}
							color={props.color}
							style={{ marginLeft: 8, marginTop: "-18px" }} // Adjust the marginTop as needed
						/>
					)}
				</div>
			}
		/>
	))({
		"&.Mui-selected": {
			color: colors.primary[100],
		},
		textTransform: "none",
	});

	const handleMainTabChange = (event, newValue) => {
		setSelectedMainTab(newValue);
		setSelectedSecondaryTab(0); // Reset secondary tab when changing main tab
		switch (newValue) {
			case 0:
				navigateTo("/materialhandling/");
				break;
			case 1:
				if (isProcedureSuggestionsAdministrator) {
					navigateTo("/materialhandling/updates/newImprov");
				} else {
					navigateTo("/materialhandling/updates/myImprov");
				}
				break;
			case 2:
				navigateTo("/materialhandling/approval/pending");
				break;
			default:
				break;
		}
	};

	const handleSecondaryTabChangeProcedure = (event, newValue) => {
		setSelectedSecondaryTab(newValue);
		switch (newValue) {
			case 0:
				if (isProcedureSuggestionsAdministrator) {
					navigateTo("/materialhandling/updates/newImprov");
				} else {
					navigateTo("/materialhandling/updates/myImprov");
				}
				break;
			case 1:
				if (isProcedureSuggestionsAdministrator) {
					navigateTo("/materialhandling/updates/myImprov");
				} else {
					navigateTo("/materialhandling/updates/verified");
				}
				break;
			case 2:
				if (isProcedureSuggestionsAdministrator) {
					navigateTo("/materialhandling/updates/verified");
				} else {
					navigateTo("/materialhandling/updates/declined");
				}
				break;
			case 3:
				navigateTo("/materialhandling/updates/declined");
				break;
			default:
				break;
		}
	};
	return (
		<div>
			<Tabs
				value={selectedMainTab}
				onChange={handleMainTabChange}
				TabIndicatorProps={{
					style: {
						backgroundColor: "#2555d4",
					},
				}}
				TabScrollButtonProps={{
					style: {
						backgroundColor: "#2555d4",
					},
				}}
			>
				<StyledTab
					label={
						<Translate String="MaterialHandling.MaterialHandling">
							Material Handling
						</Translate>
					}
				/>
				<StyledTab
					label={
						<Translate String="MaterialHandling.MaterialHandlingImprovements">
							Material Handling Improvements
						</Translate>
					}
				/>
				<StyledTab
					label={
						<Translate String="MaterialHandling.PendingApproval">
							Pending Approval
						</Translate>
					}
				/>
			</Tabs>

			{selectedMainTab === 0 && <></>}
			{selectedMainTab === 1 && (
				<Tabs
					value={selectedSecondaryTab}
					onChange={handleSecondaryTabChangeProcedure}
					TabIndicatorProps={{
						style: {
							backgroundColor: "#2555d4",
						},
					}}
					TabScrollButtonProps={{
						style: {
							backgroundColor: "#2555d4",
						},
					}}
					sx={{ marginTop: "10px" }}
				>
					{isProcedureSuggestionsAdministrator && (
						<StyledTab
							label={<Translate String="Task.Improvement.New.Improvements" />}
						/>
					)}
					<StyledTab
						label={<Translate String="Task.Improvement.My.Improvements" />}
						color="error"
					/>
					<StyledTab
						label={
							<Translate String="Task.Improvement.Verified.Improvements" />
						}
						color="success"
					/>
					<StyledTab
						label={
							<Translate String="Task.Improvement.Declined.Improvements" />
						}
					/>
				</Tabs>
			)}

			<Routes>
				<Route
					path="/materialhandling/approval/pending"
					component={ShowUpdatedProcedures}
				/>
				<Route
					path="/materialhandling/updates/newImprov"
					render={() => <ProcedureUpdates page="newImprov" />}
				/>
				<Route
					path="/materialhandling/updates/myImprov"
					render={() => <ProcedureUpdates page="myImprov" />}
				/>
				<Route
					path="/materialhandling/updates/verified"
					render={() => <ProcedureUpdates page="verified" />}
				/>
				<Route
					path="/materialhandling/updates/declined"
					render={() => <ProcedureUpdates page="declined" />}
				/>
				{/* <Route path="/materialhandling/edit/:id" component={EditTask} /> */}
			</Routes>
			<Outlet />
		</div>
	);
};

export default MaterialHandlingPage;
